import React, { useEffect, useState } from 'react'
import { Route, useHistory, useLocation, useParams } from 'react-router-dom'
import { graphql, useFragment } from 'react-relay/hooks'
import { FormattedDate } from 'react-intl'
import { pick } from 'lodash'
import { useStorage, usePage, useAnalytics } from 'hooks'
import ReportPeriod from '../ReportPeriod'
import { applyConfigReportSections } from '../utils/templates'
import IndexRedirect from '../../router/IndexRedirect'
import EmptyState from '../../common/EmptyState'
import generateReportSections from './insights/sections/generateReportSections'
import ReportResolution from '../resolve/ReportResolution'
import { findReportIntegration } from '../utils/resolutions/general'
import ReportSection from './ReportSection'
import CustomSwitch from '../../router/CustomSwitch'

const ANOMALY_REPORT_KIND = 'scrutinize:json:anomaly-report:1'
const REPORT_ROUTE = '/orgs/:orgSlug/companies/:companyId/reports/:reportId'
const excludedSectionKinds = ['anomaly', 'transactionByType']

export default function ReportViewContainer({
  report,
  resolutionSection,
  resolutionAnomalyView,
  onAnomalyViewChange,
  onResolutionSectionChange,
}) {
  const history = useHistory()
  const location = useLocation()
  const { orgSlug, companyId, reportId } = useParams()
  const storage = useStorage()
  const analytics = useAnalytics()
  const { setHeader } = usePage()
  const [output, setOutput] = useState(null)
  const [loadingPromise, setLoadingPromise] = useState(null)
  const [sections, setSections] = useState(null)

  const reportUrl = `/orgs/${orgSlug}/companies/${companyId}/reports/${reportId}`

  const data = useFragment(
    graphql`
      fragment ReportViewContainer_report on Report {
        rowId
        createdAt
        periodStart
        periodEnd
        config
        reportUserFiles {
          nodes {
            userFile {
              id
              rowId
              kind
            }
          }
        }
        reportIntegrations {
          nodes {
            integration {
              kind
            }
          }
        }
      }
    `,
    report,
  )

  const anomalyReportFile = data.reportUserFiles.nodes.find(
    (file) => file.userFile.kind === ANOMALY_REPORT_KIND,
  )

  const configSections = data.config?.sections

  // only include resolution for intuit integration reports
  const includeResolution = !!findReportIntegration(data?.reportIntegrations, 'intuit')

  // load anomaly report
  useEffect(() => {
    async function loadFile(id) {
      const file = await storage.get(id)

      // display the output
      setOutput(file)

      // track it
      analytics.track('Report Viewed', { reportId: data.rowId })

      setLoadingPromise(null)
    }

    setLoadingPromise(loadFile(anomalyReportFile.userFile.rowId))
  }, [anomalyReportFile, storage, data, analytics])

  // build report sections
  useEffect(() => {
    if (output) {
      const { reportMetadata } = output

      const reportSections = generateReportSections({
        report: output,
        reportUrl,
        history,
        location,
        configSections,
        context: 'table',
      })

      const finalReportSections = applyConfigReportSections({
        configSections,
        reportSections,
        reportEndDate: reportMetadata.reportPeriod.endDate,
      })

      setSections(finalReportSections)

      // set header values from report output
      const headerSections = [
        ...finalReportSections
          //filter subsections out of report menu
          .filter((reportSection) => !excludedSectionKinds.includes(reportSection.kind))
          .map((reportSection) =>
            pick(reportSection, ['label', 'value', 'route', 'onSelect', 'kind']),
          ),
      ]

      if (includeResolution) {
        headerSections.unshift({
          label: 'Anomaly Resolution',
          value: 'anomalyResolution',
          route: 'anomaly-resolution',
          kind: 'resolution',
          onSelect() {
            history.push(`${reportUrl}/${this.route}`)
          },
        })
      }

      // build info items
      const info = [
        {
          label: 'Period',
          value: reportMetadata.reportPeriod,
          render: (value) => <ReportPeriod value={value} />,
        },
        {
          label: 'Date',
          value: data.createdAt,
          render: (value) => <FormattedDate value={value} timeZone="America/Chicago" />,
        },
        { label: 'Transactions', value: reportMetadata.transactionVolume },
      ]

      setHeader({
        reportSections: headerSections,
        info,
      })
    }
  }, [output, history, location, data, configSections, includeResolution, reportUrl, setHeader])

  if (loadingPromise) {
    // Suspense: throw the promise
    throw loadingPromise
  }

  //null check for state
  if (!output || !sections) {
    return null
  }

  //actually no sections to display
  if (!sections.length) {
    return <EmptyState message="Great job! No anomalies were found in this report." />
  }

  const defaultPath = includeResolution ? '/anomaly-resolution' : '/transaction-insights'

  return (
    <>
      <IndexRedirect path={REPORT_ROUTE} to={defaultPath} />
      <CustomSwitch>
        <Route exact path={`${REPORT_ROUTE}/anomaly-resolution`}>
          <ReportResolution
            report={report}
            resolutionSection={resolutionSection}
            anomalyView={resolutionAnomalyView}
            onResolutionSectionChange={onResolutionSectionChange}
            onAnomalyViewChange={onAnomalyViewChange}
          />
        </Route>
        {sections.map((section) => {
          const route = `${REPORT_ROUTE}/${section.route}`

          return (
            <Route path={route} key={section.label}>
              <ReportSection route={route} reportSection={section} report={report} />
            </Route>
          )
        })}
      </CustomSwitch>
    </>
  )
}
